import axios from "axios";

// Text search over the community feed, matching the iOS/Android apps.
//
// Mobile searches a dedicated Typesense `community_feed` collection (see the
// iOS `TypeSense.searchCommunityFeed` / `createCommunityUrl`) by
// `title,location,user_display_name`, filtered to `item_type:[Hike,Recording]`
// (comments are excluded from search results). The web app never queried this
// collection, so community results were missing from web search. This helper
// adds that query using the same Typesense host/key the rest of the web search
// already uses (`window.typesenseUrl` / `window.typesenseSearchApiKey`) — the
// community feed lives on the same Typesense cluster (iOS falls back to the
// primary host/key when no separate community host is configured).

// Raw document shape as stored in the Typesense `community_feed` collection
// (snake_case, mirrors the iOS `CommunityFeedDocument` CodingKeys).
export interface CommunityFeedDocument {
  id: string;
  item_id?: number | null;
  item_type?: string | null; // "Hike" | "Recording" | "Comment"
  item_url?: string | null;
  title?: string | null;
  location?: string | null;
  static_map?: string | null;
  obfuscated_slug?: string | null;
  distance_meters?: number | null;
  duration_minutes?: number | null;
  elevation_gain_meters?: number | null;
  geo_location?: number[] | null;
  user_display_name?: string | null;
  user_full_name?: string | null;
  user_avatar_url?: string | null;
  user_id?: number | null;
  created_at?: number | null;
}

// Normalised camelCase item the Search UI renders.
export interface CommunityFeedSearchItem {
  id: string;
  itemId: number | null;
  itemType: string | null;
  title: string;
  location: string | null;
  itemUrl: string | null;
  staticMap: string | null;
  obfuscatedSlug: string | null;
  distanceMeters: number | null;
  durationMinutes: number | null;
  elevationGainMeters: number | null;
  userDisplayName: string | null;
  userFullName: string | null;
  userAvatarUrl: string | null;
}

// Item types surfaced in search, matching iOS (`item_type:[Hike,Recording]`).
export const COMMUNITY_FEED_SEARCH_ITEM_TYPES = ["Hike", "Recording"] as const;

const num = (value: unknown): number | null =>
  typeof value === "number" && !Number.isNaN(value) ? value : null;

const str = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

// Deterministic boundary: Typesense document -> UI item. Pure + unit-tested.
export function communityDocumentToSearchItem(
  doc: CommunityFeedDocument
): CommunityFeedSearchItem {
  return {
    id: String(doc.id),
    itemId: num(doc.item_id),
    itemType: str(doc.item_type),
    title: str(doc.title) ?? "",
    location: str(doc.location),
    itemUrl: str(doc.item_url),
    staticMap: str(doc.static_map),
    obfuscatedSlug: str(doc.obfuscated_slug),
    distanceMeters: num(doc.distance_meters),
    durationMinutes: num(doc.duration_minutes),
    elevationGainMeters: num(doc.elevation_gain_meters),
    userDisplayName: str(doc.user_display_name),
    userFullName: str(doc.user_full_name),
    userAvatarUrl: str(doc.user_avatar_url),
  };
}

// Map a raw Typesense response into normalised items, dropping any that have no
// link to navigate to. Tolerant of the loosely-typed Typesense JSON.
export function parseCommunityFeedResponse(data: any): CommunityFeedSearchItem[] {
  const hits = data?.hits;
  if (!Array.isArray(hits)) return [];
  return hits
    .map((hit: any) => hit?.document)
    .filter((doc: any) => doc && doc.id != null)
    .map((doc: CommunityFeedDocument) => communityDocumentToSearchItem(doc))
    .filter((item: CommunityFeedSearchItem) => item.itemUrl !== null);
}

function createCommunityFeedUrl(query: string, perPage: number): string {
  const host = (window as any).typesenseUrl;
  const urlBase = "https://" + host + "/collections/community_feed/documents/search";
  const url = new URL(urlBase);
  url.searchParams.append("q", query);
  url.searchParams.append("query_by", "title,location,user_display_name");
  url.searchParams.append("per_page", String(perPage));
  // Mirror iOS: search hikes + recordings, exclude comments.
  url.searchParams.append(
    "filter_by",
    `item_type:[${COMMUNITY_FEED_SEARCH_ITEM_TYPES.join(",")}]`
  );
  return url.href;
}

// Fetch community feed results for a text query. Returns [] on any failure so
// search degrades gracefully (the collection/key may be unavailable).
export async function searchCommunityFeed(
  query: string,
  perPage: number = 40
): Promise<CommunityFeedSearchItem[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  try {
    const response = await axios({
      url: createCommunityFeedUrl(trimmed.toLowerCase(), perPage),
      headers: {
        "x-typesense-api-key": (window as any).typesenseSearchApiKey,
      },
    });
    return parseCommunityFeedResponse(response.data);
  } catch (e) {
    console.error("Community feed search failed:", e);
    return [];
  }
}
